import { useEffect, useState } from 'react'

/**
 * One tooltip layer for the whole app. Any element with `data-tip="…"` (or a
 * plain `title`, which is migrated to data-tip on first hover so the browser's
 * own delayed tooltip never doubles up) shows an instant, styled tooltip on
 * hover AND on keyboard focus, positioned to stay inside the viewport.
 * Write tips as "what it does — and what happens next", not the label again.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<{ text: string; x: number; y: number; below: boolean } | null>(null)
  useEffect(() => {
    let current: Element | null = null
    const show = (el: Element) => {
      if (el.hasAttribute('title') && !el.hasAttribute('data-tip')) { el.setAttribute('data-tip', el.getAttribute('title') ?? ''); el.removeAttribute('title') }
      const text = el.getAttribute('data-tip')
      if (!text) return
      const r = el.getBoundingClientRect()
      const below = r.top < 48
      setTip({ text, x: Math.min(Math.max(8, r.left + r.width / 2), window.innerWidth - 8), y: below ? r.bottom + 6 : r.top - 6, below })
      current = el
    }
    const hide = () => { current = null; setTip(null) }
    const find = (t: EventTarget | null) => (t instanceof Element ? t.closest('[data-tip],[title]') : null)
    const onOver = (e: MouseEvent) => { const el = find(e.target); if (el && el !== current) show(el); else if (!el && current) hide() }
    const onFocus = (e: FocusEvent) => { const el = find(e.target); if (el) show(el) }
    const onOut = (e: MouseEvent) => { const el = find(e.target); if (el && el === current && !(e.relatedTarget instanceof Element && el.contains(e.relatedTarget))) hide() }
    document.addEventListener('mouseover', onOver, true)
    document.addEventListener('mouseout', onOut, true)
    document.addEventListener('focusin', onFocus, true)
    document.addEventListener('focusout', hide, true)
    document.addEventListener('scroll', hide, true)
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide() })
    return () => { document.removeEventListener('mouseover', onOver, true); document.removeEventListener('mouseout', onOut, true); document.removeEventListener('focusin', onFocus, true); document.removeEventListener('focusout', hide, true); document.removeEventListener('scroll', hide, true) }
  }, [])
  if (!tip) return null
  return <div className={`tooltip ${tip.below ? 'below' : ''}`} role="tooltip" style={{ left: tip.x, top: tip.y }}>{tip.text}</div>
}
